import cs from 'classnames';
import React from 'react';
import {
  Button, Col, Form, ProgressBar, Row,
} from 'react-bootstrap';
import { AudioType, FileToUpload, ManageListCallbacks, UploadFilesModalStatus } from '../../../types';
import { getRemoveButtonStateByStatus } from '../uploadFilesItemUtils';
import classNames from './UploadFilesAudioItem.module.scss';

export interface UploadFilesAudioItemProps {
    fileToUpload: FileToUpload;
    manageListCallbacks?: ManageListCallbacks;
    onAudioTypeChange?: (aliasName: string, audioType: AudioType) => void;
    isLoading?: boolean;
    disabled?: boolean;
}

const getClassNameAndText = (
  fileToUpload: FileToUpload,
): { statusClassName: string, statusText: string } => {
  const { status, errors, progress } = fileToUpload;
  switch (status) {
    case UploadFilesModalStatus.Ready:
      return {
        statusClassName: classNames.uploadStatusReady,
        statusText: 'Ready',
      };
    case 'Converting' as UploadFilesModalStatus:
      return {
        statusClassName: classNames.uploadStatusConverting,
        statusText: `Converting... ${progress}%`,
      };
    case UploadFilesModalStatus.Uploading:
      return {
        statusClassName: classNames.uploadStatusUploading,
        statusText: `Uploading... ${progress}%`,
      };
    case UploadFilesModalStatus.Error:
      return {
        statusClassName: classNames.uploadStatusError,
        statusText: `${errors?.[0]?.message ?? 'Unknown error'}`,
      };
    case UploadFilesModalStatus.Uploaded:
      return {
        statusClassName: classNames.uploadStatusUploaded,
        statusText: 'Uploaded',
      };
    default:
      return {
        statusClassName: classNames.uploadStatusError,
        statusText: 'Unknown error',
      };
  }
};

export function UploadFilesAudioItem(props: UploadFilesAudioItemProps) {
  const {
    fileToUpload, manageListCallbacks, onAudioTypeChange, isLoading, disabled,
  } = props;

  const isRemoveButtonDisabled = isLoading || getRemoveButtonStateByStatus(fileToUpload.status);
  const { statusClassName, statusText } = getClassNameAndText(fileToUpload);
  const isTypeInvalid = !fileToUpload.audioType && fileToUpload.status === UploadFilesModalStatus.Ready;
  const showProgress = fileToUpload.status === UploadFilesModalStatus.Uploading
    || (fileToUpload.status as string) === 'Converting';

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as AudioType;
    if (newType && onAudioTypeChange) {
      onAudioTypeChange(fileToUpload.aliasName, newType);
    }
  };

  return (
    <li className={classNames.listItem}>
      <Row>
        <Col md={5} className={cs(classNames.column, classNames.fileName)}>
          <div className={classNames.label}>
            File:
          </div>
          <div className={cs(classNames.text, classNames.fileNameText)}>
            {fileToUpload.file.name}
          </div>
        </Col>
        <Col md={3} className={cs(classNames.column, classNames.type)}>
          <div className={classNames.label}>
            Type:
          </div>
          <Form.Control
            as="select"
            size="sm"
            value={fileToUpload.audioType ?? ''}
            onChange={handleTypeChange}
            disabled={disabled || fileToUpload.status !== UploadFilesModalStatus.Ready}
            className={cs(classNames.typeSelect, { [classNames.typeSelectInvalid]: isTypeInvalid })}
          >
            <option value="">Select type</option>
            <option value="music">music</option>
            <option value="effect">effect</option>
          </Form.Control>
        </Col>
        <Col md={3} className={cs(classNames.column, classNames.status)}>
          <div className={classNames.label}>
            Status:
          </div>
          <div
            className={cs(classNames.text, statusClassName)}
            title={statusText}
          >
            {statusText}
          </div>
        </Col>
        <Col md={1} className={cs(classNames.column, classNames.action)}>
          <Button
            disabled={isRemoveButtonDisabled || disabled}
            size="sm"
            onClick={() => manageListCallbacks?.onDelete?.(fileToUpload)}
          >
            X
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={6} className={cs(classNames.column, classNames.title)}>
          <div className={classNames.label}>
            Title:
          </div>
          <div className={cs(classNames.text, classNames.titleText)}>
            {fileToUpload.name}
          </div>
        </Col>
        <Col md={6} className={cs(classNames.column, classNames.aliasName)}>
          <div className={classNames.label}>
            Alias:
          </div>
          <div className={cs(classNames.text, classNames.aliasNameText)}>
            {fileToUpload.aliasName}
          </div>
        </Col>
      </Row>
      {showProgress && (
        <Row>
          <Col md={12} className={classNames.progressBar}>
            <ProgressBar
              striped
              variant={(fileToUpload.status as string) === 'Converting' ? 'warning' : 'success'}
              now={fileToUpload.progress}
              label={`${fileToUpload.progress}%`}
            />
          </Col>
        </Row>
      )}
    </li>
  );
}
